import { useQuery } from '@tanstack/react-query';
import { waitForTransactionReceipt } from '@wagmi/core';
import { useChainId } from 'wagmi';
import { decodeEventLog } from 'viem';
import { verifiedPoolsPositionManagerAbi } from '@generated/generated';
import { wagmiConfig } from '@/onchain/OnchainProviders';
import { ZERO_ADDR } from '@/utils/constants';
import { getAllPositionManagerAddresses } from '@/onchain/utils/hookConfig';
import type { TransactionReceipt } from 'viem';

const QUERY_KEY_LATEST_POSITION = (txHash?: `0x${string}`) => ['useLatestPositionId', txHash];

/**
 * Fetches the latest position ID from a mint position transaction hash
 * @param txHash - The transaction hash
 * @param chainId - The chain ID
 * @returns The latest position ID or null if an error occurs or txHash is not provided or
 * txHash is not a mint position transaction
 */
async function fetchLatestPositionId(txHash?: `0x${string}`, chainId?: number) {
  if (!txHash || !chainId) {
    // If no txHash, return the last position ID from the list
    return null;
  }

  try {
    // Wait for transaction receipt using wagmiConfig
    const receipt = (await waitForTransactionReceipt(wagmiConfig, {
      hash: txHash,
    })) as TransactionReceipt;

    // Get all position manager addresses (V1 and V2)
    const positionManagerAddresses = getAllPositionManagerAddresses(chainId);

    // Find the Transfer event from any position manager (from address 0x0 indicates mint)
    const events = receipt.logs
      .filter((log) => {
        // Check if log is from any position manager
        return positionManagerAddresses.some(
          (pmAddress) => log.address.toLowerCase() === pmAddress.toLowerCase(),
        );
      })
      .map((log) => {
        try {
          return decodeEventLog({
            abi: verifiedPoolsPositionManagerAbi,
            data: log.data,
            topics: log.topics,
          });
        } catch {
          console.error('Error decoding event log:', log);
        }
      })
      // we only want the token Transfer event from posm to the user
      .filter((event) => event?.eventName === 'Transfer' && event.args.from === ZERO_ADDR);

    if (events.length === 1) {
      // we should have exactly one transfer event (minting a new NFT for the user)
      const event = events[0];
      if (event && 'id' in event.args) {
        // return the id of the new NFT
        return event.args.id;
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting position ID from transaction:', error);
  }
}

export function useLatestPositionId(txHash?: `0x${string}`) {
  const chainId = useChainId();

  // when the add liquidity confirmation modal is open, and we try to get the latest position id
  // using waitForTransactionReceipt, we do not want to show the loading state on the add liquidity page
  // so we use useQuery instead of useSuspenseQuery
  const { data: latestPositionId } = useQuery({
    queryKey: QUERY_KEY_LATEST_POSITION(txHash),
    queryFn: async () => fetchLatestPositionId(txHash, chainId),
    enabled: !!txHash,
  });

  return latestPositionId;
}
